/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import { useAPIError } from '../../context';
import { getProjects, addProject } from './service';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { useNodeDimensions } from '../../utils/hooks/useNodeDimensions';

import {
    InternalGrid,
    ModalProvider,
    ThemeDefault,
    Text,
    Box,
    TrashIcon,
    DaButton,
    ButtonGroup,
    ModalControl,
    InformationIcon,
    PlusIcon,
    SkeletonItem,
} from '@paygreen/paygreen-ui';
import { Helmet } from 'react-helmet';
import { H1Title, ProjectCard, ErrorBoundary } from '../../components';
import { IsDesktop } from '../../utils/tools';
import ProjectCardSkeleton from './ProjectCardSkeleton';
import ProjectFilter from './ProjectFilter';

const AllProject = ({ t }) => {
    const renderCardConfig = ({ isActive }) => {
        const cardConfig = {
            colorPallet: isActive === '0' ? 'wab' : null,
            colorWab: 'white20',
            shadowSize: isActive === '1' ? 'lg' : 'none',

            blockWidth: 'md',
            style: {
                minHeight: '475px',
            },
        };

        return cardConfig;
    };

    const { addError } = useAPIError();

    const [isLoading, setIsLoading] = useState(true);

    const [projects, setProjects] = useState();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    // we create a ref using a special hook to get width from InternalGrid component
    const [ref, { width }] = useNodeDimensions(isMounted.current);

    const fetchData = (filters) => {
        getProjects(filters)
            .then((res) => {
                setProjects(res.data._embedded.project);
                setIsLoading(false);
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
                setIsLoading(false);
            });
    };

    useEffect(() => {
        isMounted.current = true;

        if (isMounted.current) {
            fetchData();
        }

        return () => {
            isMounted.current = false;
        };
    }, []);

    const renderCardContent = (project, renderIcon) => {
        return (
            <InternalGrid
                displayType="flex"
                justifyContent="center"
                childrenMarginLateral="sm"
                childrenMarginTop="md"
                alignItems="center"
                justifyItems="center"
                style={{ width: '100%' }}
            >
                {renderActionButton(project, renderIcon)}
            </InternalGrid>
        );
    };

    const handleAddToMyProject = async (name, idProject) => {
        try {
            setIsLoading(true);

            await addProject({ idCarbonProject: idProject });

            fetchData();

            const index = projects.findIndex(
                (item) => item.idProject === idProject,
            );

            const copyArr = [...projects];
            copyArr[index].isUserProject = true;

            setProjects(copyArr);
            setIsLoading(false);

            toast(
                <Text colorPallet="theme" textSize="xs">
                    <Trans
                        i18nKey="contributions.globalView.globalCard.addProjectToFavorite"
                        values={{ project: name }}
                        components={{ bold: <strong /> }}
                    >
                        Le projet <bold>{{ name }}</bold> a bien été ajouté dans
                        vos projets 🎉
                    </Trans>
                </Text>,
            );
        } catch (error) {
            addError(
                error?.response?.data.detail,
                error?.response?.data.status,
            );

            setIsLoading(false);
        }
    };

    const renderCard = () => {
        if (projects?.length) {
            return projects?.map((project) => (
                <SkeletonItem
                    colorWab="grey10"
                    radiusSize="sm"
                    blockWidth="auto"
                    isLoading={isLoading}
                    key={project.idProject}
                >
                    <ModalProvider>
                        <ProjectCard
                            project={project}
                            projects={projects}
                            blockWidth="md"
                            setProjects={setProjects}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            userProjects={false}
                            fetchData={fetchData}
                            renderCardContent={renderCardContent}
                            renderCardConfig={renderCardConfig}
                        />
                    </ModalProvider>
                </SkeletonItem>
            ));
        }

        return (
            <Text align="center" marginTop="xl" marginBottom="xl" textSize="sm">
                {t('noDataFound')}
            </Text>
        );
    };

    const renderActionButton = ({
        idProject,
        name,
        isActive,
        isUserProject,
        isUserDefaultProject,
    }) => {
        if (isUserProject) {
            return (
                <>
                    <ButtonGroup marginBottom="xs" marginTop="xs">
                        <ModalControl hasOnClickControl>
                            <RouterLink to={`/projects/${idProject}`}>
                                <DaButton
                                    buttonSize="md"
                                    buttonStyle="line"
                                    colorPallet="theme"
                                    colorTheme="secondary"
                                    icon={<InformationIcon />}
                                >
                                    {t(
                                        'contributions.globalView.globalCard.infos',
                                    )}
                                </DaButton>
                            </RouterLink>

                            <button
                                disabled={isUserDefaultProject}
                                type="button"
                            >
                                <DaButton
                                    buttonSize="md"
                                    colorPallet={
                                        isUserDefaultProject
                                            ? 'theme'
                                            : 'status'
                                    }
                                    colorStatus="danger"
                                    isDisabled={isUserDefaultProject}
                                    icon={<TrashIcon />}
                                >
                                    {t(
                                        'contributions.globalView.globalCard.removeToProject',
                                    )}
                                </DaButton>
                            </button>
                        </ModalControl>
                    </ButtonGroup>
                </>
            );
        }

        if (Number(isActive) === 0) {
            return (
                <InternalGrid
                    childrenMarginTop="lg"
                    childrenMarginBottom="sm"
                    alignItems="center"
                >
                    <Text htmlTag="strong" textSize="xs" colorPallet="theme">
                        {t('contributions.globalView.globalCard.fundedProject')}
                    </Text>
                </InternalGrid>
            );
        }

        return (
            <>
                <ButtonGroup marginBottom="xs" marginTop="xs">
                    <RouterLink to={`/projects/${idProject}`}>
                        <DaButton
                            buttonSize="md"
                            buttonStyle="line"
                            colorPallet="theme"
                            colorTheme="secondary"
                            icon={<InformationIcon />}
                        >
                            {t('contributions.globalView.globalCard.infos')}
                        </DaButton>
                    </RouterLink>

                    <button type="button">
                        <DaButton
                            onClick={() =>
                                handleAddToMyProject(name, idProject)
                            }
                            buttonSize="md"
                            colorPallet="theme"
                            colorTheme="primary"
                            icon={<PlusIcon />}
                        >
                            {t(
                                'contributions.globalView.globalCard.addToProject',
                            )}
                        </DaButton>
                    </button>
                </ButtonGroup>
            </>
        );
    };

    return (
        <>
            <InternalGrid
                displayType="grid"
                alignItems="flex-start"
                justifyItems="start"
                hasStaticWidth={false}
                gridTemplateColumns="auto"
                gridGap="xs"
            >
                <Helmet>
                    <title>
                        {t('sidebar.allProjects')} - {t('main.metaTitle')}
                    </title>
                </Helmet>
                <H1Title
                    marginTop={IsDesktop() ? 'none' : 'xs'}
                    title={t('sidebar.allProjects')}
                />
            </InternalGrid>

            <ProjectFilter
                setIsLoading={setIsLoading}
                projects={projects}
                fetchData={fetchData}
            />

            <Box marginTop="md">
                <div ref={ref}>
                    <InternalGrid
                        displayType="grid"
                        gridGap="lg"
                        justifyItems="stretch"
                        alignItems="stretch"
                        childrenMarginTop="xs"
                        childrenMarginBottom="xs"
                        gridTemplateColumns={
                            width > ThemeDefault.breakpoint.lg
                                ? 'repeat(3, 1fr)'
                                : width > ThemeDefault.breakpoint.md
                                ? 'repeat(2, 1fr)'
                                : '1fr'
                        }
                    >
                        {isLoading ? (
                            <ProjectCardSkeleton repeat={3} />
                        ) : (
                            <ErrorBoundary>{renderCard()}</ErrorBoundary>
                        )}
                    </InternalGrid>
                </div>
            </Box>
        </>
    );
};

export default withTranslation('common')(AllProject);
